import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseDropdownOptions {
  closeOnEscape?: boolean;
  closeOnEnter?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

export default function useDropdown(initialOpen = false, options: UseDropdownOptions = {}) {
  const { closeOnEscape = true, closeOnEnter = false, onOpen, onClose } = options;
  const [isOpen, setIsOpen] = useState<boolean>(initialOpen);
  const containerRef = useRef<HTMLElement | null>(null);

  const open = useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const toggle = useCallback(() => {
    setIsOpen((v) => {
      const next = !v;
      if (next) onOpen?.(); else onClose?.();
      return next;
    });
  }, [onOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e: MouseEvent) => {
      const el = containerRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) close();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') close();
      if (closeOnEnter && e.key === 'Enter') close();
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, close, closeOnEscape, closeOnEnter]);

  return { isOpen, open, close, toggle, containerRef } as const;
}
